import { useState, useEffect, useRef, useMemo } from 'react';
import { draw as d3Draw, DrawEvent } from 'd3-draw';
import * as d3 from 'd3-selection';
import 'd3-dispatch';

import {
    applyOffset,
    findClosestNode,
    flipPoint,
    useFieldViewport,
    usePlayback,
    usePlayEditor,
    useSelected,
} from '@labradorsports/play-rendering';

import { useFieldContainer } from '../providers/fieldContainer.js';
import { usePlugins } from '../providers/plugins.js';

const SHOW_STROKES = false;

export default function usePen() {
    const { readOnly } = usePlayEditor();
    const { containerRef } = useFieldContainer();

    const { fieldViewport, zoomTransform } = useFieldViewport();
    const { playing } = usePlayback();
    const { selected } = useSelected();

    const { anchors, handlePenStroke } = usePlugins();

    const strokeHandlerRef = useRef<AnyFunction>();

    const [stroke, setStroke] = useState([]);
    const [simplifiedStroke, setSimplifiedStroke] = useState([]);
    const strokeHandler = useRef<AnyFunction>();
    const strokeEndHandler = useRef<AnyFunction>();
    const dragRef = useRef(false);

    strokeHandler.current = (event: DrawEvent<Element, null>) => {
        if (dragRef.current) return;

        setStroke(event.stroke.slice());
    };

    strokeEndHandler.current = (event: DrawEvent<Element, null>) => {
        const { current } = dragRef;
        dragRef.current = false;

        if (current) return;

        setStroke(SHOW_STROKES ? event.stroke : []);

        if (SHOW_STROKES) {
            setSimplifiedStroke(event.simplifiedStroke);
        }

        if (strokeHandlerRef.current) {
            strokeHandlerRef.current(event.simplifiedStroke, event.stroke);
        }
    };

    const draw = useMemo(() => {
        return (
            d3Draw()
                // Force touchable for all devices to fix Windows 10 touch
                .touchable(() => true)
                .filter((event: PointerEvent) => {
                    return SHOW_STROKES || event.pointerType === 'pen';
                })
                .on('move', (event: DrawEvent<Element, null>) => {
                    strokeHandler.current(event);
                })
                .on('end', (event: DrawEvent<Element, null>) => {
                    strokeEndHandler.current(event);
                })
        );
    }, []);

    useEffect(() => {
        if (containerRef.current) {
            const ref = containerRef.current;
            d3.select(ref)
                .call(draw)
                .on('d3dragstart', () => {
                    dragRef.current = true;
                    setStroke([]);
                });

            return () => {
                d3.select(ref).on('d3dragstart', null);
                d3.select(ref).on('.draw', null);
            };
        }

        return () => {
            //
        };
    }, [containerRef.current]);

    useEffect(() => {
        strokeHandlerRef.current = (unadjustedStroke: Point[][], unadjustedRawStroke: Point[]) => {
            if (!playing && !readOnly) {
                const flippedZoomTransform = flipPoint(zoomTransform);
                const stroke = unadjustedStroke.map((strokeParts) =>
                    strokeParts.map((point) => applyOffset(point, flippedZoomTransform))
                );
                const rawStroke = unadjustedRawStroke.map((point) => applyOffset(point, flippedZoomTransform));

                const startPoint = stroke[0][0];
                const lastPart = stroke[stroke.length - 1];
                const endPoint = lastPart[lastPart.length - 1];

                const closestStart = findClosestNode(startPoint, anchors);
                const closestEnd = findClosestNode(endPoint, anchors);

                handlePenStroke(stroke, rawStroke, closestStart, closestEnd);
            }
        };
    }, [selected, fieldViewport, playing, readOnly, anchors, zoomTransform]);

    return {
        activeStroke: stroke,
        simplifiedStroke,
    };
}
